import easing from '../helpers/easing';
import UnitBezier from '@mapbox/unitbezier';

const createMixFunction = (description) => {
  let fn;

  if (description[0] === 'bezier') {
    const bezier = new UnitBezier(...description.slice(1));

    fn = (p) => bezier.solve(p);
  } else {
    fn = easing[description[1]];
  }

  return fn;
};

function capitalizeFirstLetter(string) {
  return string.charAt(0).toUpperCase() + string.slice(1);
}

function unCapitalizeFirstLetter(string) {
  return string.charAt(0).toLowerCase() + string.slice(1);
}

const getValue = (preset, property, type) => {
  return preset[`${type}${capitalizeFirstLetter(property)}`];
};

const cutEverythingForBehavior = (preset, behaviorType) => {
  const keys = Object.keys(preset);

  const data = [];

  for (let i = 0; i < keys.length; i++) {
    if (keys[i].indexOf(behaviorType) >= 0) {
      const key = unCapitalizeFirstLetter(keys[i].substring(behaviorType.length));

      data[key] = preset[keys[i]];
    }
  }

  return data;
};

const createBehaviorProperties = (preset, medium, behaviorType) => {
  // FLUIDITY
  let fluidity = 0;

  if (preset.fluidityIsOn) {
    const fn = createMixFunction(getValue(preset, 'fluidityFunction', behaviorType));
    const value = fn(medium);

    fluidity =
      getValue(preset, 'fluidityFrom', behaviorType) + (getValue(preset, 'fluidityTo', behaviorType) - getValue(preset, 'fluidityFrom', behaviorType)) * value;
  }

  // PEN FORCE BRUSH SIZE
  let penForceBrushSizeFunction;

  if (getValue(preset, 'penForceBrushSizeIsOn', behaviorType)) {
    const fn = createMixFunction(getValue(preset, 'penForceBrushSizeFunction', behaviorType));

    penForceBrushSizeFunction = (p) => {
      const value = fn(p);
      return (
        getValue(preset, 'penForceBrushSizeFrom', behaviorType) +
        (getValue(preset, 'penForceBrushSizeTo', behaviorType) - getValue(preset, 'penForceBrushSizeFrom', behaviorType)) * value
      );
    };
  } else {
    penForceBrushSizeFunction = () => 1.0;
  }

  // PEN FORCE SPLAT RADIUS SIZE
  let penForceSplatRadiusSizeFunction;

  if (getValue(preset, 'penForceSplatRadiusSizeIsOn', behaviorType)) {
    const fn = createMixFunction(getValue(preset, 'penForceSplatRadiusSizeFunction', behaviorType));

    penForceSplatRadiusSizeFunction = (p) => {
      const value = fn(p);
      return (
        getValue(preset, 'penForceSplatRadiusSizeFrom', behaviorType) +
        (getValue(preset, 'penForceSplatRadiusSizeTo', behaviorType) - getValue(preset, 'penForceSplatRadiusSizeFrom', behaviorType)) * value
      );
    };
  } else {
    penForceSplatRadiusSizeFunction = () => 1.0;
  }

  // PEN FORCE ALPHA MIXING
  let penForceAlphaFunction;

  if (getValue(preset, 'penForceAlphaIsOn', behaviorType)) {
    const fn = createMixFunction(getValue(preset, 'penForceAlphaFunction', behaviorType));

    penForceAlphaFunction = (p) => {
      const value = fn(p);
      return (
        getValue(preset, 'penForceAlphaFrom', behaviorType) +
        (getValue(preset, 'penForceAlphaTo', behaviorType) - getValue(preset, 'penForceAlphaFrom', behaviorType)) * value
      );
    };
  } else {
    penForceAlphaFunction = () => 1.0;
  }

  // MIN ALPHA
  let minAlphaFunction = undefined;

  if (getValue(preset, 'minAlphaFunction', behaviorType)) {
    const fn = createMixFunction(getValue(preset, 'minAlphaFunction', behaviorType));

    minAlphaFunction = (x) => {
      const value = fn(x);
      return (
        getValue(preset, 'minAlphaFrom', behaviorType) + (getValue(preset, 'minAlphaTo', behaviorType) - getValue(preset, 'minAlphaFrom', behaviorType)) * value
      );
    };
  }

  // MAX ALPHA
  let maxAlphaFunction = undefined;

  if (getValue(preset, 'maxAlphaFunction', behaviorType)) {
    const fn = createMixFunction(getValue(preset, 'maxAlphaFunction', behaviorType));

    maxAlphaFunction = (x) => {
      const value = fn(x);
      return (
        getValue(preset, 'maxAlphaFrom', behaviorType) + (getValue(preset, 'maxAlphaTo', behaviorType) - getValue(preset, 'maxAlphaFrom', behaviorType)) * value
      );
    };
  }

  // ALPHA
  let alphaFunction = undefined;

  if (getValue(preset, 'alphaFunction', behaviorType)) {
    const fn = createMixFunction(getValue(preset, 'alphaFunction', behaviorType));

    alphaFunction = (a, b, x) => {
      const enhancedValue = fn(x);

      let from = a;
      let to = b;

      if (getValue(preset, 'alphaInvertedArgument', behaviorType)) {
        from = b;
        to = a;
      }

      return from + (to - from) * enhancedValue;
    };
  }

  return {
    ...cutEverythingForBehavior(preset, behaviorType),
    fluidity,
    penForceBrushSizeFunction,
    penForceSplatRadiusSizeFunction,
    penForceAlphaFunction,
    minAlphaFunction,
    maxAlphaFunction,
    alphaFunction,
  };
};

const calculateSettings = (state) => {
  const presetKey = state.controlling.defaultPresets[state.controlling.presetType];
  const preset = state.presets[presetKey];

  const paintIndex = state.controlling.buckets[state.controlling.selectedBucket];

  // DEPLETION
  let depletionFunction;

  if (preset.depletionIsOn) {
    let fn;

    if (preset.depletionFunction[0] === 'bezier') {
      const bezier = new UnitBezier(...preset.depletionFunction.slice(1));

      fn = (p) => bezier.solve(p);
    } else {
      fn = easing[preset.depletionFunction[1]];
    }

    depletionFunction = (splatsPerSegment, avgSpeed) => {
      if (avgSpeed < preset.depletionMaxSpeed) {
        const normalizedValue = 1 - avgSpeed / preset.depletionMaxSpeed;
        const enhancedValue = fn(normalizedValue);

        return Math.min(Math.floor(1 + splatsPerSegment * enhancedValue), splatsPerSegment);
      }

      return 1;
    };
  } else {
    depletionFunction = (splatsPerSegment) => splatsPerSegment;
  }

  // LIMITATIONS
  let limitationsFunction = undefined;

  if (preset.limitationsIsOn) {
    let fn;

    if (preset.limitationsFunction[0] === 'bezier') {
      const bezier = new UnitBezier(...preset.limitationsFunction.slice(1));

      fn = (p) => bezier.solve(p);
    } else {
      fn = easing[preset.limitationsFunction[1]];
    }

    limitationsFunction = (length) => {
      const normalizedValue = 1 - length / preset.limitationsMaxLength;
      const enhancedValue = fn(normalizedValue);

      return preset.limitationsFrom + (1 - preset.limitationsFrom) * enhancedValue;
    };
  }

  const settings = {
    ...preset,
    medium: state.painting.medium,
    force: state.painting.force,
    getForceFromPen: state.painting.getForceFromPen,
    global: state.properties.global,
    paintIndex,
    ...state.properties.paint,
    ...state.properties.light,

    eyeDirectionX: state.properties.light.eyeDirection[0],
    eyeDirectionY: state.properties.light.eyeDirection[1],
    eyeDirectionZ: state.properties.light.eyeDirection[2],
    lightDirectionX: state.properties.light.lightDirection[0],
    lightDirectionY: state.properties.light.lightDirection[1],
    lightDirectionZ: state.properties.light.lightDirection[2],

    depletionFunction,
    limitationsFunction,

    primary: createBehaviorProperties(preset, state.painting.medium, 'primary'),
    secondary: createBehaviorProperties(preset, state.painting.medium, 'secondary'),
  };

  console.log('SETTINGS', settings);

  return settings;
};

export default calculateSettings;
